import React, { useState, useEffect, useMemo, useRef } from "react";
import { DataTable, clientSortRows } from "./ui";
import { useParams, useNavigate } from "react-router-dom";
import "./addon-integration.css";
import "./subscription/SubscriptionFlow.css";
import ConfirmModal from "./Modals/ConfirmModal";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { api } from "../services";
import { saveFlashMessage } from "../utils/flashMessage";
import {
  resolveDateLocale,
  formatLocalizedDateTimeParts,
} from "../utils/formatDateTime";
import { getLocalizedPlatformName } from "../utils/platformNames";
import SubscriptionConnectBanner from "./subscription/SubscriptionConnectBanner";
import { subscriptionConnectErrorMessage } from "../utils/formatApiValidationError";

const isCatalogOrOrderAgentAddon = (appType, name) => {
  const type = String(appType || "").toUpperCase();
  const label = String(name || "").toLowerCase();
  return (
    type === "DIGITAL_CATALOG" ||
    type === "CHAT_AGENT" ||
    type === "ORDER_AGENT" ||
    label.includes("digital catalog") ||
    label.includes("الكتالوج الرقمي") ||
    label.includes("order agent") ||
    label.includes("chat agent") ||
    label.includes("وكيل الطلبات")
  );
};

const normalizeId = (val) => {
  if (val == null || val === "") return null;
  if (typeof val === "string") return val;
  if (typeof val === "object") return val._id || val.id || val.$oid || null;
  return String(val);
};

// Status API rows have no `_id`; never key by array index (breaks after client sort).
const getAddonRowKey = (addon) => {
  if (!addon) return "";
  const branchId = normalizeId(addon.branchId);
  const addOnAppId = normalizeId(addon.addOnAppId);
  if (branchId != null && addOnAppId != null) {
    return `${branchId}:${addOnAppId}`;
  }
  return addon._id ? String(normalizeId(addon._id) || addon._id) : "";
};

function AddOnIntegration() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("translation");
  const isArabic = i18n.language === "ar";
  const dateLocale = resolveDateLocale(i18n.language);
  const [loading, setLoading] = useState(true);
  const [addOnsData, setAddOnsData] = useState([]);
  const [dateRange, setDateRange] = useState({ dateFrom: "", dateTo: "" });
  const [selectedStatus, setSelectedStatus] = useState("ALL");
  const [storesOptions, setStoresOptions] = useState([]);
  const [selectedStoreId, setSelectedStoreId] = useState("");
  const didAutoSelectStore = useRef(false);
  const [storesPayload, setStoresPayload] = useState(null);
  const [storesList, setStoresList] = useState([]);
  const [branchesOptions, setBranchesOptions] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState("");
  const [categoriesOptions, setCategoriesOptions] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [categorySearch, setCategorySearch] = useState("");
  const [debouncedCategorySearch, setDebouncedCategorySearch] = useState("");
  const [activeTab, setActiveTab] = useState("ALL");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasPrevPage, setHasPrevPage] = useState(null);
  const [hasNextPage, setHasNextPage] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [addOnsLoading, setAddOnsLoading] = useState(false);
  const [tableSortColumn, setTableSortColumn] = useState("branchName");
  const [sortDirection, setSortDirection] = useState("asc");
  const [toggleStates, setToggleStates] = useState({});
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingToggleId, setPendingToggleId] = useState(null);
  const [selectedCatalogs, setSelectedCatalogs] = useState({});
  const [formattedData, setformattedData] = useState([]);
  const [slugValues, setSlugValues] = useState({});
  const [editingSlug, setEditingSlug] = useState({});
  const [slugErrors, setSlugErrors] = useState({});
  const [currentAddonType, setCurrentAddonType] = useState(null); // Store the current addon's appType
  const [currentAddon, setCurrentAddon] = useState(null);
  const [enabledByStore, setEnabledByStore] = useState({});

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  useEffect(() => {
    const h = setTimeout(() => {
      setDebouncedCategorySearch((categorySearch || "").trim());
    }, 700);
    return () => clearTimeout(h);
  }, [categorySearch]);

  useEffect(() => {
    fetchAddOnBranchesStatus(page, limit);
  }, [
    debouncedSearch,
    dateRange,
    selectedStatus,
    selectedBranchId,
    selectedCategoryId,
    activeTab,
    selectedStoreId,
    page,
    id,
  ]);

  useEffect(() => {
    fetchStores();
    refreshEnabledCounts();
  }, [id]);

  useEffect(() => {
    if (didAutoSelectStore.current || !storesList.length) return;
    const connectedId = Object.keys(enabledByStore).find((storeId) => enabledByStore[storeId] > 0);
    if (!connectedId) return;
    didAutoSelectStore.current = true;
    setSelectedStoreId(connectedId);
  }, [enabledByStore, storesList]);

  const fetchStores = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await api.get("/stores/my", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStoresPayload(response.data);
      const stores = response.data?.stores || [];
      setStoresList(stores);
      const options = stores.map((s) => ({
        _id: s?._id,
        name: {
          en: s?.name || s?.name_en || s?.displayName || "",
          ar: s?.name_ar || s?.displayNameAr || s?.name || "",
        },
      }));
      setStoresOptions(options);
      if (stores[0]?._id) {
        setSelectedStoreId((current) => current || stores[0]._id);
      }
    } catch (err) {
      setStoresOptions([]);
    }
  };

  const refreshEnabledCounts = async () => {
    if (!id) return;
    try {
      const token = localStorage.getItem("token");
      const response = await api.get("/add-ons/branches/status", {
        headers: { Authorization: `Bearer ${token}` },
        params: { addOnAppId: id, page: 1, limit: 500 },
      });
      const rows = response.data?.data || [];
      const counts = {};
      rows.forEach((row) => {
        const storeId = String(row.storeId || "");
        if (!storeId) return;
        counts[storeId] = (counts[storeId] || 0) + (row.isConnected ? 1 : 0);
      });
      setEnabledByStore(counts);
    } catch (err) {
      setEnabledByStore({});
    }
  };

  const updateBranchesForStore = (storeId) => {
    const store = storesList.find((s) => s?._id === storeId);
    const branches = store?.branches || [];
    const options = branches.map((b) => ({
      _id: b?._id,
      name: {
        en: b?.name?.en || b?.name || b?.displayName || "",
        ar: b?.name?.ar || b?.name_ar || b?.displayNameAr || b?.name || "",
      },
    }));
    setBranchesOptions(options);
  };

  const fetchCategoriesForStore = async (storeId, searchTerm = "") => {
    if (!storeId) {
      setCategoriesOptions([]);
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      params.set("page", "1");
      params.set("limit", "100");
      params.set("storeId", storeId);
      if ((searchTerm || "").trim())
        params.set("search", (searchTerm || "").trim());

      const response = await api.get(`/categories?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const cats = Array.isArray(response.data?.categories)
        ? response.data.categories
        : Array.isArray(response.data?.data?.categories)
        ? response.data.data.categories
        : [];

      const options = cats.map((c) => ({
        _id: c?._id,
        name: {
          en: c?.name?.en || c?.name || c?.displayName || "",
          ar: c?.name?.ar || c?.name_ar || c?.displayNameAr || c?.name || "",
        },
      }));
      setCategoriesOptions(options);
    } catch (err) {
      console.error(
        "Error fetching categories:",
        err.response?.data || err.message
      );
      setCategoriesOptions([]);
    }
  };

  const handleBrandSelect = (opt) => {
    didAutoSelectStore.current = true;
    const storeId = opt?._id || "";
    setSelectedStoreId(storeId);
    updateBranchesForStore(storeId);
    setSelectedBranchId("");
    fetchCategoriesForStore(storeId, "");
    setSelectedCategoryId("");
    setPage(1);
  };

  const fetchAddOnBranchesStatus = async (page = 1, limit = 10) => {
    try {
      setAddOnsLoading(true);
      const token = localStorage.getItem("token");
      
      // First, validate user has access to this add-on
      const availableAppsRes = await api.get("/available-apps/categorized", {
        headers: { Authorization: `Bearer ${token}` },
      });

      const addons = availableAppsRes.data.categories?.addOns?.apps || [];
      const currentAddon = addons.find(addon => {
        return addon._id === id || addon.id === id || String(addon._id) === String(id);
      });
      
      if (!currentAddon) {
        // Add-on not found - redirect with message
        saveFlashMessage(
          `${t("Please contact the support team to enable this feature for your account.")}`,
          "error"
        );
        navigate("/apps-management", { replace: true });
        return;
      }

      // Store the appType for use in modal and other places
      setCurrentAddonType(currentAddon.appType);
      setCurrentAddon(currentAddon);

      // Check if add-on is connected
      if (!currentAddon.isConnected) {
        // Add-on not connected - redirect with message
        saveFlashMessage(
          `${t("Please contact the support team to enable this feature for your account.")}`,
          "error"
        );
        navigate("/apps-management", { replace: true });
        return;
      }
      
      const response = await api.get("/add-ons/branches/status", {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          addOnAppId: id,
          page,
          limit,
          ...(selectedStoreId ? { storeId: selectedStoreId } : {}),
          ...(selectedBranchId ? { branchId: selectedBranchId } : {}),
          ...(selectedStatus && selectedStatus !== "ALL"
            ? { status: selectedStatus }
            : {}),
        },
      });
      const data = response.data?.data || [];
      const pagination = response.data?.pagination || {};
      const formattedData = response.data;
      setAddOnsData(data);
      setformattedData(formattedData);
      setTotalPages(pagination.totalPages || 1);
      setTotal(pagination.totalItems || 0);
      setHasNextPage(pagination.hasNextPage || false);
      setHasPrevPage(pagination.hasPrevPage || false);
      setAddOnsLoading(false);
      return response.data;
    } catch (err) {
      console.error(
        "Error fetching add-ons branches status:",
        err.response?.data || err.message
      );
      setAddOnsLoading(false);
    }
  };

  useEffect(() => {
    // Pre-select catalogs based on integratedCatalogId and initialize slug values
    if (addOnsData && addOnsData.length > 0) {
      const newSelected = {};
      const newToggleStates = {};
      const newSlugValues = {};

      addOnsData.forEach((addon) => {
        const addonId = getAddonRowKey(addon);
        if (!addonId) return;

        // Initialize all to null first
        newSelected[addonId] = null;

        // Initialize toggle based on isConnected
        newToggleStates[addonId] = addon.isConnected;

        // Initialize slug value from backend
        if (addon.storeSlug) {
          newSlugValues[addonId] = addon.storeSlug;
        }

        // Match catalog by integratedCatalogId
        if (
          addon.catalogs &&
          addon.catalogs.length > 0 &&
          addon.integratedCatalogId
        ) {
          const integratedCatalogId = normalizeId(addon.integratedCatalogId);
          const integratedCatalog = addon.catalogs.find(
            (cat) => normalizeId(cat.id || cat._id) === integratedCatalogId
          );
          if (integratedCatalog) {
            newSelected[addonId] = {
              _id: normalizeId(integratedCatalog.id || integratedCatalog._id),
              name: {
                en:
                  integratedCatalog.name_en ||
                  integratedCatalog.name ||
                  integratedCatalog,
                ar:
                  integratedCatalog.name_ar ||
                  integratedCatalog.name ||
                  integratedCatalog,
              },
            };
          }
        }
      });

      setSelectedCatalogs(newSelected);
      setToggleStates(newToggleStates);
      setSlugValues(newSlugValues);
    }
  }, [addOnsData]);

  useEffect(() => {
    if (!selectedStoreId) return;
    updateBranchesForStore(selectedStoreId);
    fetchCategoriesForStore(selectedStoreId, debouncedCategorySearch);
  }, [selectedStoreId, debouncedCategorySearch, storesList]);

  const formatPlatform = (value) => getLocalizedPlatformName(value, t);

  const formatDateTime = (value) =>
    formatLocalizedDateTimeParts(value, dateLocale, { withSeconds: true });

  const handleToggleClick = (addonId) => {
    if (!selectedCatalogs[addonId]) return;

    const isCurrentlyEnabled = !!toggleStates[addonId];
    if (isCurrentlyEnabled) {
      // Disable immediately — no confirmation
      setToggleStates((prev) => ({
        ...prev,
        [addonId]: false,
      }));
      handleToggleAction(addonId, false);
      return;
    }

    // Enabling requires confirmation (EN/AR via getModalContent)
    setPendingToggleId(addonId);
    setShowConfirmModal(true);
  };

  const cancelToggleEnable = () => {
    setShowConfirmModal(false);
    setPendingToggleId(null);
  };

  const confirmToggleEnable = (actionData) => {
    const addonId = actionData ?? pendingToggleId;
    if (addonId !== null && addonId !== undefined) {
      setToggleStates((prev) => ({
        ...prev,
        [addonId]: true,
      }));
      handleToggleAction(addonId, true);
    }
    setShowConfirmModal(false);
    setPendingToggleId(null);
  };

  // Helper function to get modal content based on add-on type
  const getModalContent = () => {
    const appType = String(currentAddonType || "").toUpperCase();
    const addOnName = String(
      formattedData?.filters?.addOnNames?.[0] || ""
    ).toLowerCase();

    const isDigitalCatalog =
      appType === "DIGITAL_CATALOG" ||
      addOnName.includes("digital catalog") ||
      addOnName.includes("الكتالوج الرقمي");

    const isOrderAgent =
      appType === "CHAT_AGENT" ||
      appType === "ORDER_AGENT" ||
      addOnName.includes("order agent") ||
      addOnName.includes("chat agent") ||
      addOnName.includes("وكيل الطلبات");

    if (isDigitalCatalog) {
      return {
        title: t("AddOnIntegration.enableDigitalCatalogTitle"),
        message: t("AddOnIntegration.enableDigitalCatalogMessage"),
      };
    }
    if (isOrderAgent) {
      return {
        title: t("AddOnIntegration.enableOrderAgentTitle"),
        message: t("AddOnIntegration.enableOrderAgentMessage"),
      };
    }

    return {
      title: t("AddOnIntegration.enableAddonTitle"),
      message: t("AddOnIntegration.enableAddonMessage"),
    };
  };

  const buildTogglePayload = (addon, { catalogId, enabled, slug } = {}) => {
    const payload = {
      addOnAppId: normalizeId(addon.addOnAppId),
      branchId: normalizeId(addon.branchId),
      catalogId: normalizeId(catalogId),
    };

    if (enabled !== undefined) {
      payload.enabled = enabled;
    }
    if (slug !== undefined) {
      payload.slug = slug;
    }

    return payload;
  };

  const findAddonByRowKey = (addonId) =>
    addOnsData.find((addon) => getAddonRowKey(addon) === addonId);

  const handleToggleAction = (addonId, isEnabled) => {
    const addon = findAddonByRowKey(addonId);
    const selectedCatalog = selectedCatalogs[addonId];

    if (!addon || !selectedCatalog) {
      console.error("Missing addon or catalog data");
      return;
    }

    makeToggleApiCall(
      buildTogglePayload(addon, {
        catalogId: selectedCatalog._id || selectedCatalog.id,
        enabled: isEnabled,
      }),
      addonId,
      null,
      null,
      { notifyToggle: true }
    );
  };

  const makeToggleApiCall = async (
    payload,
    triggerAddonId = null,
    brandName = null,
    newSlugValue = null,
    { notifyToggle = false } = {}
  ) => {
    try {
      const token = localStorage.getItem("token");
      const response = await api.post("/add-ons/branches/toggle", payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const addon = addOnsData.find(
        (a) =>
          normalizeId(a.addOnAppId) === normalizeId(payload.addOnAppId) &&
          normalizeId(a.branchId) === normalizeId(payload.branchId)
      );

      if (addon) {
        const addonId = triggerAddonId || getAddonRowKey(addon);
        const enabledAt = response.data?.data?.enabledAt;

        if (payload.enabled !== undefined) {
          setToggleStates((prev) => ({
            ...prev,
            [addonId]: payload.enabled,
          }));
          if (notifyToggle) {
            toast.success(
              payload.enabled
                ? t("AddOnIntegration.enableSuccess")
                : t("AddOnIntegration.disableSuccess"),
              { autoClose: 3000 }
            );
            const storeId = String(addon.storeId || "");
            if (storeId) {
              setEnabledByStore((prev) => ({
                ...prev,
                [storeId]: Math.max(0, (prev[storeId] || 0) + (payload.enabled ? 1 : -1)),
              }));
            }
          }
        }
        
        // Use the enabledAt value for display - addon will show the response value
        if (enabledAt !== undefined) {
          addon.enabledAt = enabledAt;
        }
        
        // Update the storeSlug in the addon data for all matching brands
        if (newSlugValue && brandName) {
          addOnsData.forEach((otherAddon) => {
            if (otherAddon.storeName === brandName) {
              otherAddon.storeSlug = newSlugValue;
            }
          });
        }
      }
    } catch (err) {
      console.error(
        "Error toggling add-on:",
        err.response?.data || err.message
      );

      // Revert toggle if enable/disable API failed
      if (notifyToggle && triggerAddonId != null && payload.enabled !== undefined) {
        setToggleStates((prev) => ({
          ...prev,
          [triggerAddonId]: !payload.enabled,
        }));
        toast.error(
          subscriptionConnectErrorMessage(err.response?.data, t)
            || err.response?.data?.message
            || t("AddOnIntegration.toggleFailed"),
          { autoClose: 4000 }
        );
      }
      
      // Revert optimistic updates if slug save failed
      if (triggerAddonId && brandName && newSlugValue) {
        const revertedSlugValues = { ...slugValues };
        
        addOnsData.forEach((otherAddon) => {
          const otherAddonId = getAddonRowKey(otherAddon);
          if (otherAddon.storeName === brandName && otherAddonId) {
            // Restore original value from addon data or remove if no original
            if (otherAddon.storeSlug) {
              revertedSlugValues[otherAddonId] = otherAddon.storeSlug;
            } else {
              delete revertedSlugValues[otherAddonId];
            }
          }
        });
        
        setSlugValues(revertedSlugValues);
        
        // Show error message (you can customize this)
        console.error("Failed to update slug, reverting changes");
      }
    }
  };

  const handleCatalogSelect = (addonId, selectedCatalog) => {

    // Check if catalog is empty (cleared)
    const isEmpty =
      !selectedCatalog || !selectedCatalog._id || selectedCatalog._id === "";

    setSelectedCatalogs({
      ...selectedCatalogs,
      [addonId]: isEmpty ? null : selectedCatalog,
    });

    const addon = findAddonByRowKey(addonId);

    if (addon) {
      makeToggleApiCall(
        buildTogglePayload(addon, {
          catalogId: isEmpty
            ? null
            : selectedCatalog._id || selectedCatalog.id,
          enabled: toggleStates[addonId] ?? addon.isConnected ?? false,
        }),
        addonId
      );
    }
  };

  const validateSlug = (slug) => {
    const slugRegex = /^[a-z0-9-]+$/;
    return slugRegex.test(slug);
  };

  const handleSlugChange = (addonId, newSlug) => {
    setSlugValues({
      ...slugValues,
      [addonId]: newSlug,
    });

    // Validate slug
    if (newSlug && !validateSlug(newSlug)) {
      setSlugErrors({
        ...slugErrors,
        [addonId]: t("Reports.slugValidation.invalidFormat"),
      });
    } else if (!newSlug) {
      setSlugErrors({
        ...slugErrors,
        [addonId]: t("Reports.slugValidation.required"),
      });
    } else {
      const newErrors = { ...slugErrors };
      delete newErrors[addonId];
      setSlugErrors(newErrors);
    }
  };

  const handleSlugSave = (addonId) => {
    const slug = slugValues[addonId];
    if (!slug || !validateSlug(slug)) {
      return;
    }

    const addon = findAddonByRowKey(addonId);
    const selectedCatalog = selectedCatalogs[addonId];

    if (addon) {
      const brandName = addon.storeName;
      const newSlugValues = { ...slugValues };

      addOnsData.forEach((otherAddon) => {
        const otherAddonId = getAddonRowKey(otherAddon);
        if (otherAddon.storeName === brandName && otherAddonId) {
          newSlugValues[otherAddonId] = slug;
        }
      });

      setSlugValues(newSlugValues);

      makeToggleApiCall(
        buildTogglePayload(addon, {
          catalogId: selectedCatalog?._id || selectedCatalog?.id || null,
          enabled: toggleStates[addonId] || false,
          slug,
        }),
        addonId,
        brandName,
        slug
      );
      setEditingSlug({
        ...editingSlug,
        [addonId]: false,
      });
    }
  };

  const handleSlugCancel = (addonId) => {
    // Restore original value
    const addon = findAddonByRowKey(addonId);
    if (addon && addon.storeSlug) {
      setSlugValues({
        ...slugValues,
        [addonId]: addon.storeSlug,
      });
    } else {
      const newSlugValues = { ...slugValues };
      delete newSlugValues[addonId];
      setSlugValues(newSlugValues);
    }

    // Clear error and exit edit mode
    const newErrors = { ...slugErrors };
    delete newErrors[addonId];
    setSlugErrors(newErrors);
    setEditingSlug({
      ...editingSlug,
      [addonId]: false,
    });
  };

  const showSlugColumn =
    currentAddonType === "DIGITAL_CATALOG" ||
    formattedData?.filters?.addOnNames?.[0] === "Digital Catalog";

  const addonLabel =
    currentAddon?.name || formattedData?.filters?.addOnNames?.[0] || "";
  const showStoreSettings =
    Boolean(currentAddon || currentAddonType) &&
    !isCatalogOrOrderAgentAddon(currentAddonType || currentAddon?.appType, addonLabel);

  const pageTitle = useMemo(() => {
    if (currentAddonType === "CHAT_AGENT" || currentAddonType === "ORDER_AGENT") {
      return t("Reports.AI Order Agent");
    }
    if (currentAddonType === "DIGITAL_CATALOG") {
      return t("Reports.DigitalCatalog");
    }
    return t("sidebar.appManagement");
  }, [currentAddonType, t]);

  const sortedAddOnsData = useMemo(
    () =>
      clientSortRows(addOnsData, tableSortColumn, sortDirection, (addon, col) => {
        switch (col) {
          case "storeName":
            return (addon.storeName || "").toLowerCase();
          case "branchName":
            return (
              isArabic
                ? addon.branchName_ar || addon.branchName_en || addon.branchName
                : addon.branchName_en || addon.branchName
            ).toLowerCase();
          case "integratedAt":
            return addon.enabledAt ? new Date(addon.enabledAt).getTime() : 0;
          default:
            return "";
        }
      }),
    [addOnsData, tableSortColumn, sortDirection, isArabic]
  );

  const columns = useMemo(() => {
    const cols = [
      {
        id: "branchName",
        header: t("addAppPage.addAppTable.branchName"),
        sortable: true,
        minWidth: 160,
        render: (addon) =>
          isArabic
            ? addon.branchName_ar || addon.branchName_en || addon.branchName
            : addon.branchName_en || addon.branchName,
      },
      {
        id: "status",
        header: t("addAppPage.addAppTable.status"),
        width: 160,
        minWidth: 140,
        render: (addon) => {
          const addonId = getAddonRowKey(addon);
          const connected = toggleStates[addonId] ?? addon.isConnected;
          return (
            <span className={`aa-spill ${connected ? "ok" : "off"}`}>
              {connected ? t("addAppPage.storeConnected") : t("addAppPage.storeNotConnected")}
            </span>
          );
        },
      },
      {
        id: "integratedAt",
        header: t("addAppPage.addAppTable.lastUpdated"),
        sortable: true,
        nowrap: true,
        minWidth: 150,
        render: (addon) => {
          if (!addon.enabledAt) return <span className="sf-hint">—</span>;
          const { date, time } = formatDateTime(addon.enabledAt);
          return (
            <div className="oh-table-date-cell" dir="ltr">
              <span className="oh-table-date-cell__date">{date}</span>
              <span className="oh-table-date-cell__time">{time}</span>
            </div>
          );
        },
      },
      {
        id: "catalogs",
        header: t("AddOnIntegration.catalogColumn"),
        minWidth: 240,
        render: (addon) => {
          const addonId = getAddonRowKey(addon);
          const catalogs = Array.isArray(addon.catalogs) ? addon.catalogs : [];
          const hasCatalogs = catalogs.length > 0;
          const selectedId = selectedCatalogs[addonId]?._id || "";
          return (
            <div onClick={(e) => e.stopPropagation()}>
              <select
                className={`aa-catalog-select${selectedId ? " has" : ""}`}
                value={selectedId}
                disabled={!hasCatalogs}
                aria-label={t("AddOnIntegration.catalogColumn")}
                onChange={(event) => {
                  const match = catalogs.find((cat) => String(normalizeId(cat.id || cat._id)) === event.target.value);
                  handleCatalogSelect(addonId, match ? {
                    _id: normalizeId(match.id || match._id),
                    name: {
                      en: match.name_en || match.name || match,
                      ar: match.name_ar || match.name || match,
                    },
                  } : null);
                }}
              >
                <option value="">
                  {hasCatalogs ? t("AddOnIntegration.selectCatalog") : t("AddOnIntegration.noCatalogs")}
                </option>
                {catalogs.map((cat) => {
                  const catId = normalizeId(cat.id || cat._id);
                  const label = isArabic
                    ? cat.name_ar || cat.name_en || cat.name
                    : cat.name_en || cat.name || cat.name_ar;
                  return <option key={catId} value={catId}>{label}</option>;
                })}
              </select>
            </div>
          );
        },
      },
      {
        id: "enable",
        header: t("addAppPage.enable"),
        align: "center",
        width: 150,
        minWidth: 130,
        render: (addon) => {
          const addonId = getAddonRowKey(addon);
          const on = !!toggleStates[addonId];
          const locked = !selectedCatalogs[addonId];
          const branchLabel = isArabic
            ? addon.branchName_ar || addon.branchName_en || addon.branchName
            : addon.branchName_en || addon.branchName;
          return (
            <div className="aa-flagcell" onClick={(e) => e.stopPropagation()}>
              <label className={`aa-switch${locked ? " is-ro" : ""}`} title={locked ? t("AddOnIntegration.selectCatalogTooltip") : undefined}>
                <input
                  type="checkbox"
                  role="switch"
                  checked={on}
                  disabled={locked}
                  aria-label={t("addAppPage.enableBranch", { branch: branchLabel })}
                  onChange={() => handleToggleClick(addonId)}
                />
                <span className="track" aria-hidden="true" />
                <span className="aa-swl">{on ? t("addAppPage.enabled") : t("addAppPage.off")}</span>
              </label>
            </div>
          );
        },
      },
    ];

    if (showSlugColumn) {
      cols.push({
        id: "slug",
        header: t("Reports.TableHeaders.slug"),
        minWidth: 240,
        render: (addon) => {
          const addonId = getAddonRowKey(addon);
          return (
            <div onClick={(e) => e.stopPropagation()}>
              {editingSlug[addonId] ? (
                <div className="slug-input-container">
                  <div className="slug-input-wrapper">
                    <input
                      type="text"
                      className={`slug-input ${slugErrors[addonId] ? "error" : ""}`}
                      value={slugValues[addonId] || ""}
                      onChange={(e) => handleSlugChange(addonId, e.target.value)}
                      placeholder="Enter store slug"
                      autoFocus
                    />
                    {slugErrors[addonId] && (
                      <div className="slug-error">{slugErrors[addonId]}</div>
                    )}
                  </div>
                  <div className="slug-actions">
                    <button
                      className="slug-action-btn slug-save-btn"
                      onClick={() => handleSlugSave(addonId)}
                      disabled={!!slugErrors[addonId] || !slugValues[addonId]}
                      title="Save slug"
                    >
                      ✓
                    </button>
                    <button
                      className="slug-action-btn slug-cancel-btn"
                      onClick={() => handleSlugCancel(addonId)}
                      title="Cancel editing"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  className="slug-display"
                  onClick={() =>
                    setEditingSlug({ ...editingSlug, [addonId]: true })
                  }
                  title="Click to edit slug"
                >
                  {slugValues[addonId] || addon.storeSlug || "-"}
                </div>
              )}
            </div>
          );
        },
      });
    }

    return cols;
  }, [
    t,
    isArabic,
    dateLocale,
    showSlugColumn,
    selectedCatalogs,
    toggleStates,
    editingSlug,
    slugValues,
    slugErrors,
    addOnsData,
  ]);

  const appDisplayName = (isArabic && currentAddon?.name_ar) || currentAddon?.name || pageTitle;
  const selectedStoreRecord = storesList.find((store) => String(store._id) === String(selectedStoreId));
  const selectedStoreName = selectedStoreRecord
    ? (isArabic
      ? selectedStoreRecord.name_ar || selectedStoreRecord.name?.ar || selectedStoreRecord.name
      : selectedStoreRecord.name?.en || selectedStoreRecord.name || selectedStoreRecord.name_en)
    : "";

  return (
    <div className="aa-page sf-page addon-integration-page-root">
      <button type="button" className="sf-btn link aa-back" onClick={() => navigate("/apps-management")}>
        ← {t("addAppPage.backToApps")}
      </button>
      <div className="aa-title">
        {currentAddon?.logo ? (
          <img className="aa-logo" src={currentAddon.logo} alt="" />
        ) : (
          <span className="aa-logo aa-logo-fallback">{String(appDisplayName || "L").slice(0, 1)}</span>
        )}
        <div>
          <h1>{appDisplayName}</h1>
          <p>{t("addAppPage.kindAddon")}</p>
        </div>
      </div>

      <section className="sf-section">
        <div className="sf-section-head">
          <h2>{t("addAppPage.storesTitle")}</h2>
          <span className="sf-meta">{t("AddOnIntegration.storesCount", { count: storesList.length })}</span>
        </div>
        <p className="sf-sub">{t("AddOnIntegration.storesLead", { app: appDisplayName })}</p>
        <div className="aa-stores" role="group" aria-label={t("addAppPage.storesTitle")}>
          {storesList.map((store) => {
            const on = String(store._id) === String(selectedStoreId);
            const branchCount = Array.isArray(store.branches) ? store.branches.length : 0;
            const enabled = enabledByStore[String(store._id)] || 0;
            const name = isArabic
              ? store.name_ar || store.name?.ar || store.name
              : store.name?.en || store.name || store.name_en;
            return (
              <button
                type="button"
                key={store._id}
                className={`aa-store${on ? " is-on" : ""}`}
                aria-pressed={on}
                onClick={() => handleBrandSelect({ _id: store._id })}
              >
                <span className="top"><b>{name}</b><span className="aa-radio" aria-hidden="true" /></span>
                <small>{t("AddOnIntegration.enabledCount", { count: enabled })}</small>
                <small>{t("addAppPage.storeBranchCount", { count: branchCount })}</small>
              </button>
            );
          })}
        </div>
      </section>

      {showStoreSettings ? (
        <section className="sf-section">
          <div className="sf-section-head">
            <h2>{t("addAppPage.settingsTitle")}</h2>
            <span className="sf-meta">{selectedStoreName}</span>
          </div>
          <div className="aa-integ">
            <h3>{t("addAppPage.enableBranches")}</h3>
            <p className="aa-desc">{t("AddOnIntegration.enableBranchesLead", { app: appDisplayName })}</p>
          </div>
          <p className="sf-hint">{t("addAppPage.otherSettings", { app: appDisplayName })}</p>
        </section>
      ) : null}

      <section className="sf-section aa-bsec">
        <div className="aa-bhead">
          <div>
            <h2>{t("addAppPage.branchesTitle")}</h2>
            <p className="aa-bsub">
              <b>{selectedStoreName}</b>
              {" · "}
              {t("AddOnIntegration.pickCatalogHint")}
            </p>
          </div>
          <SubscriptionConnectBanner appId={id} variant="meter" />
        </div>
        <DataTable
          tableId="addon-integration"
          hideToolbar
          className="aa-branches"
          columns={columns}
          data={sortedAddOnsData}
          rowKey={(addon) => getAddonRowKey(addon)}
          loading={addOnsLoading}
          emptyState={t("AddOnIntegration.noBranchesAvailable")}
          isRtl={isArabic}
          tableMinWidth={760}
          sort={{
            column: tableSortColumn,
            direction: sortDirection,
            onChange: ({ column, direction }) => {
              setTableSortColumn(column);
              setSortDirection(direction);
            },
          }}
          pagination={{
            page,
            pageSize: limit,
            total,
            onPageChange: (newPage) => {
              if (hasPrevPage !== null && hasNextPage !== null) {
                if (newPage < page && !hasPrevPage) return;
                if (newPage > page && !hasNextPage) return;
              }
              if (newPage >= 1 && newPage <= totalPages) {
                setPage(newPage);
              }
            },
            onPageSizeChange: (size) => {
              setLimit(size);
              setPage(1);
              fetchAddOnBranchesStatus(1, size);
            },
            pageSizeOptions: [10, 20, 50],
            showLabel: t("show"),
            ofLabel: t("of"),
            resultsLabel: t("results", { defaultValue: "results" }),
            isRtl: isArabic,
            previousLabel: t("previous"),
            nextLabel: t("next"),
          }}
        />
      </section>

      <ConfirmModal
        show={showConfirmModal}
        onHide={cancelToggleEnable}
        title={getModalContent().title}
        message={getModalContent().message}
        action={confirmToggleEnable}
        actionData={pendingToggleId}
      />
    </div>
  );
}

export default AddOnIntegration;
